import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Heart, Search, Home, Shield, Star, CheckCircle, ArrowRight } from "lucide-react";

interface OnboardingWelcomeProps {
  onComplete: () => void;
  onSkip: () => void;
  onOptOut: () => void;
}

export const OnboardingWelcome = ({ onComplete, onSkip, onOptOut }: OnboardingWelcomeProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const steps = [
    {
      icon: Heart,
      title: "Willkommen bei Fair-Stays",
      description: "Ihre faire Plattform für Unterkünfte - mehr Gewinn für Anbieter, bessere Preise für Gäste.",
      features: [
        "Transparente Preisgestaltung",
        "Direkte Kommunikation mit Gastgebern",
        "Sichere Buchungsabwicklung"
      ]
    },
    {
      icon: Search,
      title: "Suchen & Entdecken",
      description: "Finden Sie die perfekte Unterkunft mit unserer intelligenten Suchfunktion.",
      features: [
        "Detaillierte Filter & Karten",
        "Echte Bewertungen von Gästen", 
        "Sofortbuchung verfügbar"
      ]
    },
    {
      icon: Home,
      title: "Buchen & Verwalten",
      description: "Einfache Buchung und Verwaltung Ihrer Aufenthalte in einem Dashboard.",
      features: [
        "Sichere Zahlungsabwicklung",
        "Buchungsbestätigungen per E-Mail",
        "24/7 Kundensupport"
      ]
    }
  ];

  const currentStepData = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;
  const Icon = currentStepData.icon;

  const handleNext = () => {
    if (isLastStep) {
      if (dontShowAgain) {
        onOptOut();
      } else {
        onComplete();
      }
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  return (
    <div className="fixed inset-0 bg-background/95 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md mx-auto shadow-strong animate-scale-in">
        <CardHeader className="text-center pb-4">
          <div className="flex justify-center mb-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-hero">
              <Icon className="h-8 w-8 text-white" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold">{currentStepData.title}</CardTitle>
          <p className="text-muted-foreground mt-2">{currentStepData.description}</p>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Progress Dots */}
          <div className="flex justify-center space-x-2">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`h-2 w-2 rounded-full transition-smooth ${
                  index === currentStep ? 'bg-primary' : 'bg-muted'
                }`}
              />
            ))}
          </div>

          {/* Features */}
          <div className="space-y-3">
            {currentStepData.features.map((feature, index) => (
              <div key={index} className="flex items-center space-x-3 animate-fade-in" style={{ animationDelay: `${index * 100}ms` }}>
                <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                <span className="text-sm">{feature}</span>
              </div>
            ))}
          </div>

          {/* Step Indicator */}
          <div className="text-center">
            <Badge variant="secondary">
              Schritt {currentStep + 1} von {steps.length}
            </Badge>
          </div>

          {/* Action Buttons */}
            {/* Checkbox for "Don't show again" */}
            <div className="flex items-center space-x-2 mb-4">
              <Checkbox 
                id="dont-show-again" 
                checked={dontShowAgain}
                onCheckedChange={(checked) => setDontShowAgain(checked as boolean)}
              />
              <label 
                htmlFor="dont-show-again" 
                className="text-sm text-muted-foreground cursor-pointer"
              >
                Tutorial nicht mehr anzeigen
              </label>
            </div>
            
            <div className="flex flex-col space-y-3">
              <Button onClick={handleNext} className="w-full" size="lg">
                {isLastStep ? (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Los geht's!
                  </>
                ) : (
                  <>
                    Weiter
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>
              
              <div className="flex justify-between">
                <Button 
                  variant="ghost" 
                  onClick={handlePrevious}
                  disabled={currentStep === 0}
                  className="text-sm"
                >
                  Zurück
                </Button>
                <Button 
                  variant="ghost" 
                  onClick={dontShowAgain ? onOptOut : onSkip}
                  className="text-sm text-muted-foreground"
                >
                  {dontShowAgain ? 'Nicht mehr anzeigen' : 'Überspringen'}
                </Button>
              </div>
            </div>
        </CardContent>
      </Card>
    </div>
  );
};